import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { LoginContext } from "../../context/LoginContext";
import { USER_ROLES } from "../../auth/session";
import api from "../../services/api";
import { FaUserGraduate } from "react-icons/fa";
import Swal from "sweetalert2";
import "./StudentRegistrationSettings.css";

const ROOT = "/students/registration-settings";
const ACTIVE = ["PENDING", "PROCESSING"];
const LABELS = {
  PENDING: "Aguardando processamento", PROCESSING: "Em processamento",
  FAILED: "Falha; revisão necessária", AWAITING_CONFIRMATION: "Novas pendências aguardam confirmação",
  COMPLETED: "Concluída", APPROVED: "Aprovada automaticamente", ALREADY_RESOLVED: "Resolvida anteriormente",
};
const newKey = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
};

function RegistrationSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [target, setTarget] = useState(true);
  const [operation, setOperation] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const lastOperation = useRef(null);
  const command = useRef(null);
  const acting = useRef(false);
  const alive = useRef(true);
  const sequence = useRef(0);
  const controller = useRef(null);

  const refresh = useCallback(async (requestedPage = page) => {
    const current = ++sequence.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setLoading(true);
    setError("");
    try {
      const result = await api.get(ROOT, { signal: abort.signal });
      if (!alive.current || current !== sequence.current) return;
      const next = result.data;
      if (typeof next?.requireApproval !== "boolean" || !Number.isInteger(next.settingsVersion)) {
        throw new Error("Invalid settings");
      }
      setSettings(next);
      setTarget(next.requireApproval);
      const id = next.activeOperationId || lastOperation.current;
      if (id) {
        const response = await api.get(ROOT + "/operations/" + encodeURIComponent(id), {
          params: { page: requestedPage, limit: 50 }, signal: abort.signal,
        });
        if (!alive.current || current !== sequence.current) return;
        if (!Array.isArray(response.data?.items)) throw new Error("Invalid progress");
        lastOperation.current = id;
        setOperation(response.data);
        // Completion may occur between the settings and progress reads.
        if (response.data.status === "COMPLETED" && next.activeOperationId === id) {
          const effective = await api.get(ROOT, { signal: abort.signal });
          if (!alive.current || current !== sequence.current) return;
          if (typeof effective.data?.requireApproval !== "boolean" || !Number.isInteger(effective.data.settingsVersion)) throw new Error("Invalid settings");
          setSettings(effective.data);
          setTarget(effective.data.requireApproval);
        }
        if (requestedPage > Math.max(1, response.data.totalPages)) setPage(Math.max(1, response.data.totalPages));
      } else setOperation(null);
    } catch (failure) {
      if (alive.current && current === sequence.current && !abort.signal.aborted) {
        setError(failure?.response?.status === 403 ? "Acesso restrito ao administrador da empresa."
          : "Não foi possível atualizar o cadastro de alunos. Os dados exibidos são da última consulta.");
      }
    } finally {
      if (alive.current && current === sequence.current) setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    alive.current = true;
    refresh();
    return () => { alive.current = false; controller.current?.abort(); };
  }, [refresh]);

  useEffect(() => {
    if (!ACTIVE.includes(operation?.status) || loading || busy || error || uncertain || confirming) return undefined;
    const timer = setTimeout(() => refresh(), 5000);
    return () => clearTimeout(timer);
  }, [operation, loading, busy, error, uncertain, confirming, refresh]);

  const begin = () => {
    if (acting.current) return false;
    acting.current = true;
    ++sequence.current;
    controller.current?.abort();
    setLoading(false);
    setBusy(true);
    setNotice("");
    return true;
  };
  const finish = () => {
    acting.current = false;
    if (alive.current) setBusy(false);
  };
  const inspect = async (desired = target) => {
    if (!begin()) return;
    let next;
    try {
      const response = await api.post(ROOT + "/impact", { targetRequireApproval: desired });
      if (!alive.current) return;
      next = response.data;
      if (typeof next?.targetRequireApproval !== "boolean" || next.targetRequireApproval !== desired ||
        !Number.isInteger(next.pendingCount) || next.pendingCount < 0 ||
        !Number.isInteger(next.settingsVersion) || !/^[a-f0-9]{64}$/.test(next.impactVersion)) throw new Error("Invalid impact");
    } catch {
      if (alive.current) setNotice("Não foi possível consultar o impacto. Clique em Atualizar para tentar novamente.");
      return;
    } finally { finish(); }

    if (!desired && next.pendingCount > 0) {
      setConfirming(true);
      let decision;
      try {
        const count = next.pendingCount;
        decision = await Swal.fire({
          icon: "warning",
          title: "Confirmar alteração do cadastro?",
          text: `Há ${count} ${count === 1 ? "solicitação pendente" : "solicitações pendentes"}. Ao confirmar, ${count === 1 ? "ela será aprovada" : "elas serão aprovadas"} automaticamente antes de ativar o cadastro sem aprovação.`,
          showCancelButton: true,
          confirmButtonText: "Confirmar e aprovar pendentes",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#2f6fec",
        });
      } finally {
        if (alive.current) setConfirming(false);
      }
      if (!alive.current) return;
      if (!decision?.isConfirmed) {
        setTarget(settings.requireApproval);
        return;
      }
    }

    if (!alive.current) return;
    await send({ path: ROOT + "/changes", body: {
      targetRequireApproval: next.targetRequireApproval,
      settingsVersion: next.settingsVersion, impactVersion: next.impactVersion,
      idempotencyKey: newKey(),
    } });
  };

  const send = async (pending) => {
    if (!begin()) return;
    command.current = pending;
    setUncertain(false);
    try {
      const response = await api.post(pending.path, pending.body);
      if (!alive.current) return;
      lastOperation.current = response.data?.operationId || lastOperation.current;
      command.current = null;
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: response.data?.operationId
          ? "Alteração recebida. Acompanhe o andamento abaixo."
          : "Configuração atualizada com sucesso.",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
      await refresh();
    } catch (failure) {
      if (!alive.current) return;
      if (!failure?.response || failure.response.status >= 500) {
        setUncertain(true);
        setNotice("Não foi possível confirmar o resultado. Clique em Atualizar para repetir a mesma solicitação com segurança.");
      } else {
        command.current = null;
        setNotice(failure.response.status === 409
          ? "O estado mudou. Selecione a regra desejada e clique em Atualizar para consultar o novo impacto."
          : "Não foi possível concluir a solicitação. Atualize o estado e revise as pendências.");
        await refresh();
      }
    } finally { finish(); }
  };

  const retry = () => send({
    path: ROOT + "/operations/" + encodeURIComponent(operation.id) + "/retry",
    body: { idempotencyKey: newKey() },
  });
  const handleUpdate = () => {
    if (loading || busy || confirming) return;
    if (uncertain && command.current) return send(command.current);
    if (error || !settings) return refresh();
    if (operation?.status === "FAILED") return retry();
    if (operation?.status === "AWAITING_CONFIRMATION") return inspect(false);
    if (ACTIVE.includes(operation?.status) || target === settings.requireApproval) return refresh();
    return inspect(target);
  };
  const locked = loading || busy || confirming || Boolean(error) || uncertain;

  return (
    <section className="registration-settings image-policy-section" aria-labelledby="student-registration-title">
      <div className="registration-settings-heading">
        <span className="image-policy-section-icon" aria-hidden="true"><FaUserGraduate /></span>
        <div>
          <span className="image-policy-eyebrow">ACESSO POR CONVITE</span>
          <h2 id="student-registration-title">Cadastro de alunos</h2>
          <p>Defina se os cadastros enviados por link precisam de aprovação administrativa.</p>
        </div>
      </div>
      {loading && <p className="registration-settings-message" role="status">Atualizando cadastro de alunos...</p>}
      {error && <p className="registration-settings-message error" role="alert">{error}</p>}
      {notice && <p className="registration-settings-message" role="status">{notice}</p>}
      {settings && <>
        <div className="registration-settings-current">
          <div>
            <span className="registration-settings-current-label">CONFIGURAÇÃO EM VIGOR</span>
            <strong>{settings.requireApproval ? "Aprovação obrigatória" : "Cadastro sem aprovação"}</strong>
          </div>
          <span className={`registration-settings-status ${settings.requireApproval ? "manual" : "automatic"}`}>
            {settings.requireApproval ? "Revisão manual" : "Entrada automática"}
          </span>
        </div>
        <div className="registration-settings-control">
          <label className="registration-settings-toggle">
            <span>Exigir aprovação de cadastros por link externo</span>
            <input type="checkbox" checked={target}
              onChange={event => setTarget(event.target.checked)}
              disabled={locked || Boolean(operation && operation.status !== "COMPLETED")} />
          </label>
          <p>Se houver solicitações pendentes, você confirmará o impacto antes de aplicar a mudança.</p>
        </div>
      </>}
      <div className="registration-settings-primary-action">
        <button type="button" disabled={loading || busy || confirming} onClick={handleUpdate}>Atualizar</button>
      </div>
      {operation && (
        <section className="registration-settings-progress" aria-labelledby="registration-progress-title">
          <h3 id="registration-progress-title">Acompanhamento da mudança</h3>
          <p role="status">{LABELS[operation.status] || "Estado indisponível"}</p>
          <p>{operation.completed} de {operation.total} resolvidas; {operation.failed} com falha.</p>
          <progress aria-label="Solicitações resolvidas" value={operation.completed} max={Math.max(1, operation.total)} />
          <p>As aprovações concluídas são preservadas. A configuração efetiva só muda após a conclusão de todas as pendências.</p>
          {operation.status === "FAILED" && <>
            <p role="alert">Revise as solicitações com falha. A aprovação obrigatória foi mantida. Após corrigir o problema, clique em Atualizar para tentar novamente somente as pendências já confirmadas.</p>
          </>}
          {operation.status === "AWAITING_CONFIRMATION" &&
            <p>Há novas pendências. Clique em Atualizar para consultar o impacto e decidir se deseja incluí-las.</p>}
          <div className="registration-results" role="region" aria-label="Resultados detalhados das solicitações" tabIndex={0}>
            <table>
              <caption>Resultados das solicitações confirmadas</caption>
              <thead><tr><th>Solicitação</th><th>Resultado</th><th>Tentativas</th></tr></thead>
              <tbody>{operation.items.map(item => (
                <tr key={item.requestId}><td>{item.requestId}</td><td>{LABELS[item.status] || "Estado indisponível"}
                  {item.errorCode && <span> — Requer revisão ou nova tentativa.</span>}</td><td>{item.attempts}</td></tr>
              ))}</tbody>
            </table>
          </div>
          {!operation.items.length && <p>Nenhuma solicitação nesta página.</p>}
          <nav aria-label="Páginas das solicitações" className="registration-settings-actions">
            <button type="button" disabled={locked || page <= 1} onClick={() => setPage(value => value - 1)}>Anterior</button>
            <span>Página {page} de {Math.max(1, operation.totalPages)}</span>
            <button type="button" disabled={locked || page >= operation.totalPages} onClick={() => setPage(value => value + 1)}>Próxima</button>
          </nav>
        </section>
      )}
    </section>
  );
}

export default function StudentRegistrationSettings() {
  const { LoggedUser } = useContext(LoginContext);
  if (LoggedUser?.role !== USER_ROLES.ADMIN) return null;
  return <RegistrationSettingsPanel key={LoggedUser.companyId || LoggedUser.id} />;
}
